import React, { useEffect, useState, useMemo } from 'react';
import { __, _n, sprintf } from '@wordpress/i18n';
import { useForm, useWatch } from 'react-hook-form';

// store
import { storeSet, useStoreValue } from '../../store';

// services
import { ASSIGN_NOTE_MAX_LENGTH } from '../../service/permissions-service';

import './../../assets/components/ModalAssignMemberToCuesheetItem.css';

// components
import FormField from '../FormField';
import ButtonFlat from '../ButtonWrapper/components/ButtonFlat';
import ButtonFormSubmit from '../ButtonWrapper/components/ButtonFormSubmit';
import ModalDialog from '../ModalDialog';
import UserAvatar from '../UserAvatar';

/** Avatars shown on the row before the rest is collapsed into a "+N" chip. */
const VISIBLE_AVATARS = 3;

/**
 * "Assegna" on a cue sheet row.
 *
 * A cue holds a list of assignees, and the API takes it as a replace-set
 * (`POST .../cue-sheet/{itemId}/assignees`): the modal opens on whoever is
 * assigned now and saves the whole list in one call, so nothing has to be
 * diffed and two members can no longer overwrite one another. Clearing the
 * field and saving unassigns everybody, which takes the cue back to `to_work`.
 *
 * @param {array} assignees        UserRead[] currently assigned, for the avatars
 * @param {array} assigneeUserIds  their ids, which is what the form works on
 */
const ModalAssignMemberToCuesheetItem = ({
                                             projectId = '',
                                             cueSheetId = '',
                                             assignees = [],
                                             assigneeUserIds = [],
                                             headerText = __('Assegna', 'soundbetween')
                                         }) => {
    const [modalVisible, setModalVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const {
        control,
        handleSubmit,
        reset,
        formState: { isValid }
    } = useForm({
        mode: 'onChange',
        defaultValues: {
            teamMembers: [],
            note: ''
        }
    });
    const team = useStoreValue('team');
    const note = useWatch({ control, name: 'note' }) ?? '';
    const chosenMembers = useWatch({ control, name: 'teamMembers' }) ?? [];

    const currentIds = useMemo(
        () => (assigneeUserIds?.length ? assigneeUserIds : assignees.map(user => user.id)).filter(Boolean),
        [assigneeUserIds, assignees]
    );

    // The form opens on the assignees the cue already has: saving an untouched
    // form has to leave them exactly as they were.
    useEffect(() => {
        if (!modalVisible) return;

        reset({ teamMembers: currentIds, note: '' });
    }, [modalVisible, currentIds, reset]);

    const onSubmit = async (formData) => {
        setLoading(true);

        try {
            // The note is optional and only reaches the members that are being
            // added, so it is dropped when nothing changes.
            const trimmedNote = (formData.note ?? '').trim();

            await storeSet(
                'setCueSheetAssignees',
                projectId,
                cueSheetId,
                formData.teamMembers ?? [],
                trimmedNote
            );

            // This component stays mounted while the dialog closes, so the form
            // has to be cleared by hand or the note reappears on the next open.
            reset();

            // Close modal on success
            setModalVisible(false);
        } catch (error) {
            // Error is already displayed by the action's displayNetworkError
            // We just catch it here to prevent unhandled promise rejection
            console.error('Failed to assign permissions:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateNew = () => {
        setModalVisible(true);
    }

    const handleHide = () => {
        if (!modalVisible) return;
        reset();
        setModalVisible(false);
    }

    // Prepare team members options for multiselect (formatted with value/label)
    const teamOptions = useMemo(() => {
        return team.map(member => ({
            ...member,
            value: member.userId,
            label: `${member.name || ''} ${member.surname || ''}`.trim() || member.invitedEmail
        }));
    }, [team]);

    // Custom render for selected items with avatars
    const renderSelectedUser = (user, handleRemove) => {
        return (
            <>
                <UserAvatar
                    url={user.profile_picture_url}
                    name={user.label}
                />
                <span className="multiSelectDropdown__selectedItemLabel">
                    {user.label}
                </span>
                <button
                    type="button"
                    onClick={() => handleRemove(user.value)}
                    className="multiSelectDropdown__removeBtn"
                    aria-label={__('Rimuovi utente', 'soundbetween')}
                />
            </>
        );
    };

    const shown = assignees.slice(0, VISIBLE_AVATARS);
    const hidden = assignees.length - shown.length;

    return (
        <>
            <div className="assignCuesheetItem">
                {shown.length > 0 && (
                    <div className="assignCuesheetItem__avatars">
                        {shown.map(user => (
                            <UserAvatar
                                key={user.id}
                                url={user.profile_picture_url}
                                name={`${user.name || ''} ${user.surname || ''}`.trim()}
                            />
                        ))}
                        {hidden > 0 && (
                            <span
                                className="assignCuesheetItem__more"
                                title={assignees
                                    .map(user => `${user.name || ''} ${user.surname || ''}`.trim())
                                    .join(', ')}>
                                {sprintf(__('+%d', 'soundbetween'), hidden)}
                            </span>
                        )}
                    </div>
                )}
                {shown.length
                    ? <button
                        type="button"
                        className="assignCuesheetItem__addBtn"
                        onClick={handleCreateNew}
                        aria-label={__('Modifica gli assegnatari', 'soundbetween')}
                      >+</button>
                    : <ButtonFlat size="sm" onClick={handleCreateNew}>{__('Assegna', 'soundbetween')}</ButtonFlat>
                }
            </div>
            {modalVisible
                ? <ModalDialog
                    additionalClasses={['modalCreateProject']}
                    header={headerText}
                    visible={modalVisible}
                    onHide={handleHide}>
                    <form className="appForm formInModal" onSubmit={handleSubmit(onSubmit)}>
                        <FormField
                            type="multiselect"
                            name="teamMembers"
                            label={__('Seleziona Membri del Team', 'soundbetween')}
                            control={control}
                            initialOptions={teamOptions}
                            labelKey="label"
                            valueKey="value"
                            placeholder={__('Cerca membri...', 'soundbetween')}
                            displayMode="count"
                            showClearAll={true}
                            renderSelectedItem={renderSelectedUser}
                        />

                        {chosenMembers.length === 0 && currentIds.length > 0
                            ? <p className="appFormNotice">
                                {__('Salvando senza membri selezionati la cue torna “Da lavorare”.', 'soundbetween')}
                            </p>
                            : null}

                        <FormField
                            type="textarea"
                            name="note"
                            rows={7}
                            maxLength={ASSIGN_NOTE_MAX_LENGTH}
                            label={__('Aggiungi note', 'soundbetween')}
                            control={control}
                            description={chosenMembers.length > currentIds.length
                                ? sprintf(
                                    _n(
                                        '%1$s — recapitata al nuovo assegnatario',
                                        '%1$s — recapitata ai nuovi assegnatari',
                                        chosenMembers.length - currentIds.length,
                                        'soundbetween'
                                    ),
                                    `${note.length}/${ASSIGN_NOTE_MAX_LENGTH}`
                                )
                                : `${note.length}/${ASSIGN_NOTE_MAX_LENGTH}`}
                            rules={{
                                maxLength: {
                                    value: ASSIGN_NOTE_MAX_LENGTH,
                                    message: __('La nota non può superare 1024 caratteri', 'soundbetween')
                                }
                            }}
                        />

                        <div className="appFormActions">
                            <ButtonFlat size="lg" type="button" onClick={handleHide}>
                                {__('Annulla', 'soundbetween')}
                            </ButtonFlat>
                            <ButtonFormSubmit disabled={loading || !isValid}>
                                {__('Salva', 'soundbetween')}
                            </ButtonFormSubmit>
                        </div>
                    </form>
                </ModalDialog> : null}
        </>
    )
}

export default ModalAssignMemberToCuesheetItem;
